<template>
  <div class="search-shell" :class="{ 'search-shell--fill': fill }">
    <div class="search-shell__filters">
      <slot name="filters" />
    </div>

    <div
      v-if="$slots['result-header'] && showResults"
      class="search-shell__result-bar"
    >
      <slot name="result-header" />
    </div>

    <div ref="bodyRef" class="search-shell__body">
      <div v-if="showLoading" class="search-shell__state">
        <slot name="loading">
          <span class="icon"><i class="fa-solid fa-spinner fa-spin" /></span>
          <span class="ml-2">Loading…</span>
        </slot>
      </div>

      <div v-else-if="showInitial" class="search-shell__state">
        <slot name="initial">
          <span class="icon"><i class="fa-solid fa-magnifying-glass" /></span>
          <span class="ml-2">Enter a search query to see results.</span>
        </slot>
      </div>

      <div v-else-if="showEmpty" class="search-shell__state">
        <slot name="empty">
          <span class="has-text-grey is-size-7">
            {{ resolvedEmptyMessage }}
          </span>
        </slot>
      </div>

      <slot v-else name="results" />
    </div>

    <div v-if="$slots.pagination" class="search-shell__footer">
      <slot name="pagination" />
    </div>
  </div>
</template>

<script>
import { computed, ref } from 'vue'

import { useHighlight } from '@/composables/useHighlight'

export default {
  name: 'SearchShell',
  props: {
    isLoading: { type: Boolean, default: false },
    hasSearched: { type: Boolean, default: false },
    resultCount: { type: Number, default: 0 },
    searchQuery: { type: String, default: '' },
    emptyMessage: { type: String, default: '' },
    fill: { type: Boolean, default: false },
  },
  setup(props) {
    const bodyRef = ref(null)
    useHighlight(
      bodyRef,
      computed(() => props.searchQuery),
      computed(() => props.resultCount),
    )
    // Loading: first fetch only — don't hide existing results during load-more
    const showLoading = computed(
      () => props.isLoading && props.resultCount === 0,
    )
    const showInitial = computed(
      () => !props.hasSearched && !props.isLoading && props.resultCount === 0,
    )
    const showEmpty = computed(
      () => props.hasSearched && !props.isLoading && props.resultCount === 0,
    )
    const showResults = computed(() => props.resultCount > 0)

    const resolvedEmptyMessage = computed(() => {
      if (props.emptyMessage) return props.emptyMessage
      const q = props.searchQuery?.trim()
      return q
        ? `No results found for "${q}". Try a different search term.`
        : 'No results found. Try a different search term.'
    })

    return {
      bodyRef,
      showLoading,
      showInitial,
      showEmpty,
      showResults,
      resolvedEmptyMessage,
    }
  },
}
</script>

<style lang="scss" scoped>
.search-shell {
  display: flex;
  flex-direction: column;

  &--fill {
    height: 100%;
  }
}

// When not filling, body scrolls naturally with content
.search-shell:not(.search-shell--fill) .search-shell__body {
  flex: unset;
  overflow-y: visible;
  min-height: unset;
}

.search-shell__filters {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  // Balanced padding on all sides (no top-heavy asymmetry)
  padding: var(--space-3);

  &:empty {
    padding: 0;
  }
}

.search-shell__result-bar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  color: var(--text-light);
}

.search-shell__body {
  flex: 0 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding: var(--space-3);
}

.search-shell__state {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-4);
  color: var(--text-light);
}

.search-shell__footer {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-3);
  min-height: 2.5rem;
}
</style>
